import { AutoComplete, Input, Modal } from "@arco-design/web-react";
import { useEffect, useState, type ReactElement } from "react";

import { sysDeptStatusOptions } from "../config";
import {
  createEmptySysDeptMutation,
  type SysDeptOption,
  type SysStaffOption,
} from "../service/sysDeptUserService";
import type { SysDeptMutation, SysDeptTreeNode } from "../types";
import "./SysDeptUserPage.css";

type SysDeptMutationModalProps = {
  state: { type: "create"; parentId?: number } | { type: "edit"; dept: SysDeptTreeNode } | null;
  deptOptions: SysDeptOption[];
  staffOptions: SysStaffOption[];
  errorMessage: string;
  onClose: () => void;
  onClearError: () => void;
  onCreateDept: (payload: SysDeptMutation) => Promise<void>;
  onUpdateDept: (deptId: number, payload: SysDeptMutation) => Promise<void>;
};

/** 部门编辑弹窗。 */
export function SysDeptMutationModal({
  state,
  deptOptions,
  staffOptions,
  errorMessage,
  onClose,
  onClearError,
  onCreateDept,
  onUpdateDept,
}: SysDeptMutationModalProps) {
  const [payload, setPayload] = useState<SysDeptMutation>(createEmptySysDeptMutation());
  const [parentDeptText, setParentDeptText] = useState("");
  const [leaderText, setLeaderText] = useState("");
  const [approverText, setApproverText] = useState("");

  const parentDeptOptions = [
    { value: "0", name: "无上级部门" },
    ...deptOptions.map((option) => ({ value: String(option.value), name: option.label })),
  ];
  const staffAutoCompleteOptions = staffOptions.map((option) => ({
    value: String(option.value),
    name: option.label,
    searchText: option.searchText,
  }));

  useEffect(() => {
    if (!state) {
      return;
    }
    onClearError();
    if (state.type === "create") {
      const nextPayload = createEmptySysDeptMutation(state.parentId ?? 0);
      setPayload(nextPayload);
      setParentDeptText(getDeptOptionLabel(deptOptions, nextPayload.parentId));
      setLeaderText("");
      setApproverText("");
      return;
    }
    const nextPayload = {
      deptName: state.dept.deptName,
      parentId: state.dept.parentId,
      orderNum: state.dept.orderNum,
      leaderId: state.dept.leaderId,
      approverId: state.dept.approverId,
      phone: state.dept.phone,
      email: state.dept.email,
      status: state.dept.status,
    };
    setPayload(nextPayload);
    setParentDeptText(getDeptOptionLabel(deptOptions, nextPayload.parentId));
    setLeaderText(
      getStaffOptionLabel(staffOptions, nextPayload.leaderId) || state.dept.leaderName || "",
    );
    setApproverText(
      getStaffOptionLabel(staffOptions, nextPayload.approverId) || state.dept.approverName || "",
    );
  }, [state, deptOptions, staffOptions, onClearError]);

  /** 提交部门新增或编辑。 */
  async function handleOk() {
    if (!state) {
      return;
    }
    if (state.type === "create") {
      await onCreateDept(payload);
    } else {
      await onUpdateDept(state.dept.id, payload);
    }
    onClose();
  }

  return (
    <Modal
      title={state?.type === "edit" ? "编辑部门" : "新增部门"}
      visible={Boolean(state)}
      onCancel={onClose}
      onOk={handleOk}
      okText="保存"
      cancelText="取消"
      className="sys-dept-user-modal"
      unmountOnExit
    >
      {errorMessage ? <div className="sys-dept-user-error">{errorMessage}</div> : null}
      <div className="sys-dept-user-modal-grid">
        <label className="sys-dept-user-field">
          <span>部门名称</span>
          <Input
            aria-label="部门名称"
            value={payload.deptName}
            onChange={(deptName) => setPayload({ ...payload, deptName })}
          />
        </label>
        <label className="sys-dept-user-field">
          <span>父部门</span>
          <AutoComplete
            allowClear
            data={parentDeptOptions}
            filterOption={filterAutoCompleteOption}
            placeholder="请选择父部门"
            value={parentDeptText}
            inputProps={{ "aria-label": "父部门" }}
            onChange={(value) => {
              setParentDeptText(value);
              if (!value) {
                setPayload({ ...payload, parentId: 0 });
              }
            }}
            onSelect={(value) => {
              setParentDeptText(getDeptOptionLabel(deptOptions, Number(value)));
              setPayload({ ...payload, parentId: Number(value) });
            }}
          />
        </label>
        <label className="sys-dept-user-field">
          <span>排序</span>
          <input
            aria-label="排序"
            className="sys-dept-user-number-input"
            type="number"
            value={payload.orderNum}
            onChange={(event) => setPayload({ ...payload, orderNum: Number(event.target.value) })}
          />
        </label>
        <label className="sys-dept-user-field">
          <span>负责人</span>
          <AutoComplete
            allowClear
            data={staffAutoCompleteOptions}
            filterOption={filterAutoCompleteOption}
            placeholder="请选择负责人"
            value={leaderText}
            inputProps={{ "aria-label": "负责人" }}
            onChange={(value) => {
              setLeaderText(value);
              if (!value) {
                setPayload({ ...payload, leaderId: undefined });
              }
            }}
            onSelect={(value) => {
              setLeaderText(getStaffOptionLabel(staffOptions, Number(value)));
              setPayload({ ...payload, leaderId: Number(value) });
            }}
          />
        </label>
        <label className="sys-dept-user-field">
          <span>审批人</span>
          <AutoComplete
            allowClear
            data={staffAutoCompleteOptions}
            filterOption={filterAutoCompleteOption}
            placeholder="请选择审批人"
            value={approverText}
            inputProps={{ "aria-label": "审批人" }}
            onChange={(value) => {
              setApproverText(value);
              if (!value) {
                setPayload({ ...payload, approverId: undefined });
              }
            }}
            onSelect={(value) => {
              setApproverText(getStaffOptionLabel(staffOptions, Number(value)));
              setPayload({ ...payload, approverId: Number(value) });
            }}
          />
        </label>
        <label className="sys-dept-user-field">
          <span>电话</span>
          <Input aria-label="部门电话" value={payload.phone} onChange={(phone) => setPayload({ ...payload, phone })} />
        </label>
        <label className="sys-dept-user-field">
          <span>邮箱</span>
          <Input aria-label="部门邮箱" value={payload.email} onChange={(email) => setPayload({ ...payload, email })} />
        </label>
        <label className="sys-dept-user-field">
          <span>状态</span>
          <select
            aria-label="部门状态"
            className="sys-dept-user-native-select"
            value={payload.status}
            onChange={(event) => setPayload({ ...payload, status: Number(event.target.value) })}
          >
            {sysDeptStatusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      </div>
    </Modal>
  );
}

/** 按展示名、搜索文本和 ID 过滤自动补全选项。 */
function filterAutoCompleteOption(inputValue: string, option: ReactElement) {
  const keyword = inputValue.trim().toLowerCase();
  if (!keyword) {
    return true;
  }
  const props = option.props as {
    children?: string;
    extra?: { name?: string; searchText?: string };
    value?: string | number;
  };
  const text = [
    props.children,
    props.extra?.name,
    props.extra?.searchText,
    props.value,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return text.includes(keyword);
}

/** 根据部门 ID 获取自动补全展示名称。 */
function getDeptOptionLabel(options: SysDeptOption[], value: number): string {
  if (value === 0) {
    return "无上级部门";
  }
  return options.find((option) => option.value === value)?.label ?? "";
}

/** 根据员工 ID 获取自动补全展示名称。 */
function getStaffOptionLabel(options: SysStaffOption[], value?: number): string {
  if (!value) {
    return "";
  }
  return options.find((option) => option.value === value)?.label ?? "";
}
